import { Box, useSafeLayoutEffect } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useResource } from 'datx-jsonapi-react';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FadeInModalContext } from '@/components/layouts/fadeInModalContext';
import {
  NAVIGATION_RAIL_EXPANDED_WIDTH,
  NAVIGATION_RAIL_WIDTH,
  rehydrateNavigationRail,
  useNavigationRailStore,
} from '@/components/ui/shared/headers/EventHeader/components/NavigationRail/NavigationRail.store';
import { EventHeader } from '@/components/ui/shared/headers/EventHeader/EventHeader';
import { LibLynxCounterDefaults } from '@/components/ui/shared/utilities/LibLynxCounterDefaults/LibLynxCoutnerDefaults';
import { LibraryRedirect } from '@/components/ui/shared/utilities/LibraryRedirect/LibraryRedirect';
import { useAnalytics } from '@/context/Analytics';
import { OverlayType } from '@/enums/index';
import { IToastType } from '@/enums/ToastType';
import { useLogoutMutation } from '@/hooks/useLogoutMutation';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useToast } from '@/hooks/useToast';
import { queryThinEvent } from '@/queries/thin-events';
import { sessionQueryOptions } from '@/queries/upgrade/sessions';
import { StringParam } from '@/utils/url-query-string';

export const EventAppLayout: FC<{ children?: ReactNode }> = ({ children }) => {
  const toast = useToast();
  const { t } = useTranslation();
  const analytics = useAnalytics();

  const [showModalType, setShowModalType] = useState(OverlayType.NONE);

  const { eventId, router } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });

  const { data: event } = useResource(queryThinEvent(eventId));

  const onLogoutSuccess = () => {
    analytics.track('user_logout');
    toast({ status: IToastType.SUCCESS, title: t('action.loggedOut') });
  };

  const onLogoutError = () => {
    toast({ status: IToastType.ERROR, title: t('action.loggedOutError') });
  };

  const { data: session } = useQuery(sessionQueryOptions());
  const currentUserProfile = session?.main_profile;

  const [logOut] = useLogoutMutation({ onSuccess: onLogoutSuccess, onFailure: onLogoutError });

  useEffect(() => {
    if (event) {
      analytics.page(router.asPath, {
        event: event.name,
        event_id: event.id,
        event_state: event.state,
      });
    }
  }, [event, analytics]);

  // TODO: FadeInModalContext should've been a Portal
  return (
    // <TwilioChatProvider>
    <FadeInModalContext.Provider
      value={{
        showModalType: { get: showModalType, set: setShowModalType },
      }}
    >
      <LibLynxCounterDefaults />
      {/* Only encapsulates redirection in order no to clutter the layout component */}
      <LibraryRedirect event={event} />

      <EventHeader
        event={event}
        currentUserProfile={currentUserProfile}
        onLogout={() => logOut(null)}
        zIndex="banner"
      />

      {/* TODO: full-screen legacy code; remove padding and header to make place for full screen */}
      <EventAppMain>{children}</EventAppMain>
    </FadeInModalContext.Provider>
    // </TwilioChatProvider>
  );
};

// the rail is fixed, so the content has to keep its own left gap in step with it
const EventAppMain: FC<{ children?: ReactNode }> = ({ children }) => {
  const isExpanded = useNavigationRailStore((state) => state.isExpanded);
  const shouldAnimate = useNavigationRailStore((state) => state.shouldAnimate);

  // a layout effect so a reload applies the saved state before the hydrated page paints; on later
  // pages it just re-reads the value the store already holds
  useSafeLayoutEffect(() => {
    rehydrateNavigationRail();
  }, []);

  return (
    <Box
      as="main"
      pl={{ lg: isExpanded ? NAVIGATION_RAIL_EXPANDED_WIDTH : NAVIGATION_RAIL_WIDTH }}
      transition={shouldAnimate ? 'padding-left .2s ease' : undefined}
    >
      {children}
    </Box>
  );
};
